import { ClientOptions } from 'graphql-ws';

// Observe transport health without changing graphql-ws retry behavior. This
// process-local state contains no user data and emits no events on its own.
let state = 'not_connected';
let reconnectAttempts = 0;
let lastMessageAt: number | undefined;
let closeCode: number | undefined;
let wasClean: boolean | undefined;
let pageInstanceId: string | undefined;
let trackerSubscriptionEvent = 'not_observed';
let trackerSubscriptionEventAt: number | undefined;
let notificationSubscriptionEvent = 'not_observed';
let notificationSubscriptionEventAt: number | undefined;

// Bound both page memory and the diagnostic payload, even after many reconnects.
const MAX_TRANSITIONS = 32;
type TransitionEvent =
  | 'connecting'
  | 'connected'
  | 'closed'
  | 'error'
  | 'tracker_update'
  | 'tracker_error'
  | 'tracker_complete'
  | 'tracker_restart'
  | 'notification_update'
  | 'notification_error'
  | 'notification_complete'
  | 'notification_restart'
  | 'page_observed'
  | 'visibility_changed'
  | 'online'
  | 'offline'
  | 'page_restored';
interface Transition {
  at: number;
  event: TransitionEvent;
  visibility: DocumentVisibilityState;
  online: boolean;
  retry_attempt: number;
  close_code?: number;
  was_clean?: boolean;
}
const transitions: Transition[] = [];
let observingPage = false;

function recordTransition(event: TransitionEvent) {
  if (typeof window === 'undefined') return;
  transitions.push({
    at: Date.now(),
    event,
    visibility: document.visibilityState,
    online: navigator.onLine,
    retry_attempt: reconnectAttempts,
    ...(event === 'closed'
      ? { close_code: closeCode, was_clean: wasClean }
      : {}),
  });
  if (transitions.length > MAX_TRANSITIONS) transitions.shift();
}

// These listeners live with the browser's singleton Apollo client. No network
// requests or telemetry events are produced by observing page transitions.
export function observeConnectionPageLifecycle() {
  if (typeof window === 'undefined' || observingPage) return;
  observingPage = true;
  recordTransition('page_observed');
  document.addEventListener('visibilitychange', () =>
    recordTransition('visibility_changed')
  );
  window.addEventListener('online', () => recordTransition('online'));
  window.addEventListener('offline', () => recordTransition('offline'));
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) recordTransition('page_restored');
  });
}

// JSON keeps the existing scalar PostHog event-property contract. Timestamps
// are ages relative to this capture, with entries ordered oldest first.
export function getConnectionHistory() {
  const now = Date.now();
  return JSON.stringify(
    transitions.map(({ at, ...transition }) => ({
      ...transition,
      age_ms: now - at,
    }))
  );
}

export function recordTrackerSubscriptionEvent(
  event: 'update' | 'error' | 'complete' | 'restart'
) {
  // Repeated data deliveries must not evict the transitions that explain a stall.
  if (event !== 'update' || trackerSubscriptionEvent !== 'update') {
    recordTransition(`tracker_${event}`);
  }
  trackerSubscriptionEvent = event;
  trackerSubscriptionEventAt = Date.now();
}

export function recordNotificationSubscriptionEvent(
  event: 'update' | 'error' | 'complete' | 'restart'
) {
  if (event !== 'update' || notificationSubscriptionEvent !== 'update') {
    recordTransition(`notification_${event}`);
  }
  notificationSubscriptionEvent = event;
  notificationSubscriptionEventAt = Date.now();
}

export const connectionHealthListeners: ClientOptions['on'] = {
  connecting: (isRetry) => {
    state = 'connecting';
    if (isRetry) reconnectAttempts++;
    recordTransition('connecting');
  },
  connected: () => {
    state = 'connected';
    reconnectAttempts = 0;
    recordTransition('connected');
  },
  message: () => {
    lastMessageAt = Date.now();
  },
  closed: (event) => {
    state = 'closed';
    closeCode = undefined;
    wasClean = undefined;
    if (event instanceof CloseEvent) {
      closeCode = event.code;
      wasClean = event.wasClean;
    }
    recordTransition('closed');
  },
  error: () => {
    state = 'error';
    recordTransition('error');
  },
};

export function getConnectionHealth() {
  pageInstanceId ??= crypto.randomUUID();
  return {
    page_instance_id: pageInstanceId,
    websocket_state: state,
    reconnect_attempts: reconnectAttempts,
    last_message_age_ms:
      lastMessageAt === undefined ? null : Date.now() - lastMessageAt,
    last_close_code: closeCode ?? null,
    last_close_was_clean: wasClean ?? null,
    tracker_subscription_last_event: trackerSubscriptionEvent,
    tracker_subscription_event_age_ms:
      trackerSubscriptionEventAt === undefined
        ? null
        : Date.now() - trackerSubscriptionEventAt,
    notification_subscription_last_event: notificationSubscriptionEvent,
    notification_subscription_event_age_ms:
      notificationSubscriptionEventAt === undefined
        ? null
        : Date.now() - notificationSubscriptionEventAt,
    visibility: document.visibilityState,
    online: navigator.onLine,
  };
}
